import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash.isequal';
import { fetchInitialAlertAdr } from '@rsos/components/capstone/DataCards/adrDataSlice';
import {
  hideAlertPopover,
  showAlertPopover,
} from '@rsos/components/capstone/common/Map/useDeckGL/MapPopover/mapPopoverSlice';
import { DEFAULT_ZOOM_LEVEL } from '@rsos/maps/constants';
import { GOOGLE } from '@rsos/maps/constants/mapProviders';
import { fetchAlertConversationStatus } from '@rsos/reduxDataStore/src/features/alerts/chatSlice';
import { fetchAlert } from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import { setHoveredAlertID } from '@rsos/reduxDataStore/src/features/incidentInteractions/hoveredIncidentSlice';
import { filteredAlertsSelector } from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import { panMapTo } from '@rsos/utils/panMapToCoordinate';
import { captureException } from '@rsos/utils/sentry';
import usePrevious from '@rsos/utils/usePrevious';
import AlertsLayer from '../RSOSLayers/AlertsLayer';
import storeLayers from '../storeLayers';
import {
  getAlertsIconProps,
  getCommonCircleLayerProps,
  getIncidentLocations,
} from './utils';

const AlertsGL = ({ map, overlay, activeQueueTab }) => {
  const dispatch = useDispatch();
  const [hoveredAlertData, setHoveredAlertData] = useState(null);
  const [normalizedData, setNormalizedData] = useState([]);
  const alerts =
    useSelector(state => filteredAlertsSelector(state.incidents)) || {};
  const { application, currentOrg } = useSelector(state => state.sinatra.user);
  const selectedAlertID = useSelector(state => state.incidents.selectedAlertID);
  const zoomLevel = useRef(DEFAULT_ZOOM_LEVEL);

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const isAllView = useMemo(
    () => !selectedAlertID && !selectedCallerID,
    [selectedAlertID, selectedCallerID],
  );

  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const prevMapInstance = usePrevious(map.mapInstance);

  const hasGoogleMapIdChanged = useMemo(() => {
    // If the map provider isn't GOOGLE, return false immediately.
    if (mapProvider !== GOOGLE) return false;

    const prevMapId = prevMapInstance?.mapId;
    const currentMapId = map.mapInstance?.mapId;

    // Check if both id's exist and have changed.
    return Boolean(prevMapId && currentMapId && prevMapId !== currentMapId);
  }, [mapProvider, prevMapInstance?.mapId, map.mapInstance?.mapId]);

  const handleHoverAlertLocation = useCallback(
    ({ alertData }) => {
      if (map) {
        if (alertData) {
          dispatch(setHoveredAlertID(alertData?.alert?.alert_id));
          setHoveredAlertData(alertData);
          dispatch(showAlertPopover(alertData));
        } else {
          setHoveredAlertData(null);
          dispatch(setHoveredAlertID(null));
          dispatch(hideAlertPopover(null));
          overlay.setCursorDefault();
        }
      }
    },
    [dispatch, map, overlay],
  );

  const handleClickAlertLocation = useCallback(
    ({ alertData }) => {
      if (map) {
        setHoveredAlertData(null);
        dispatch(setHoveredAlertID(null));
        dispatch(hideAlertPopover(null));
        const incidentID = alertData?.alert?.alert_id;

        dispatch(
          fetchAlert({
            currentApplication: application,
            currentOrgName: currentOrg.name,
            alertID: incidentID,
            shouldSetID: true,
          }),
        ).then(() => {
          dispatch(
            fetchAlertConversationStatus({
              orgName: currentOrg.name,
              conversationID: `chat-portal-default-${
                incidentID.split('alert-')[1]
              }`,
            }),
          );
          dispatch(fetchInitialAlertAdr(incidentID));
        });
      }
    },
    [dispatch, map, overlay],
  );
  // Normalize the data
  useEffect(() => {
    if (isAllView) {
      if (Object.keys(alerts).length) {
        const locations = Object.keys(alerts)
          .map(incidentID => {
            const mostRecentLocationsAlerts = getIncidentLocations(
              alerts,
              incidentID,
            )[0];
            return {
              ...mostRecentLocationsAlerts,
              isHistoric: false,
            };
          })
          .filter(loc => loc);
        setNormalizedData(locations);
      }
    } else if (selectedAlertID) {
      const selectedAlertLocations = getIncidentLocations(
        alerts,
        selectedAlertID,
      );
      const locs = selectedAlertLocations.map((loc, idx) => {
        return {
          ...loc,
          isHistoric: idx === 0 ? false : true,
        };
      });
      setNormalizedData(locs);
    }
  }, [isAllView, alerts, selectedAlertID, dispatch, map]);

  /** define layers and properties used to draw markers and circles */
  useEffect(() => {
    if (normalizedData) {
      const alertsIconLayerProps = getAlertsIconProps(
        isAllView,
        activeQueueTab,
        selectedCallerID,
      );
      const commonCircleLayerProps = getCommonCircleLayerProps(selectedAlertID);
      const mostRecentLocation = normalizedData?.[0];

      const mostRecentCircleLayerProps = {
        data: [mostRecentLocation],
        ...commonCircleLayerProps,
      };
      const historicCircleLayerProps = {
        data:
          hoveredAlertData !== null &&
          !isEqual(hoveredAlertData, mostRecentLocation)
            ? [hoveredAlertData]
            : [],
        ...commonCircleLayerProps,
      };

      const alertsLayer = new AlertsLayer({
        data: normalizedData,
        alertsIconLayerProps,
        mostRecentCircleLayerProps,
        historicCircleLayerProps,
        autoHighlight: true,
        onHover: d => {
          return handleHoverAlertLocation({ alertData: d.object });
        },
        onClick: d => {
          return handleClickAlertLocation({ alertData: d.object });
        },
      });
      if (overlay) {
        storeLayers.save(alertsLayer);
        overlay.setLayers(storeLayers.getAll());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    normalizedData,
    hoveredAlertData,
    selectedAlertID,
    overlay,
    activeQueueTab,
    selectedCallerID,
    selectedAlertID,
  ]);
  const prevSelectedAlertID = usePrevious(selectedAlertID);
  const prevLocations = usePrevious(
    getIncidentLocations(alerts, selectedAlertID),
  );
  /** manage zooming and panning */
  useEffect(() => {
    let zoomChangeListener;

    if (map.mapInstance) {
      if (selectedAlertID) {
        const currentLocations = getIncidentLocations(alerts, selectedAlertID);

        if (prevSelectedAlertID !== selectedAlertID || hasGoogleMapIdChanged) {
          const mostRecentLoc =
            alerts[selectedAlertID]?.location_history?.[0]?.geodetic || {};
          const zoom =
            map.mapInstance.getZoom() < DEFAULT_ZOOM_LEVEL
              ? DEFAULT_ZOOM_LEVEL
              : null;

          panMapTo({
            mapProvider: GOOGLE,
            mapInstance: map.mapInstance,
            latitude: mostRecentLoc.latitude,
            longitude: mostRecentLoc.longitude,
            zoom,
          });
          /** case when an alert is selected but a new location arrives */
        } else if (
          prevSelectedAlertID === selectedAlertID &&
          currentLocations.length > prevLocations.length
        ) {
          const mostRecentLoc = currentLocations?.[0] || {};
          panMapTo({
            mapProvider: GOOGLE,
            mapInstance: map.mapInstance,
            latitude: mostRecentLoc.latitude,
            longitude: mostRecentLoc.longitude,
            zoom: null, // Don't change zoom
          });
        }
      } else {
        // Reset zoom level when transitioning to all view
        zoomLevel.current = DEFAULT_ZOOM_LEVEL;
      }

      zoomChangeListener = map.mapInstance.addListener('zoom_changed', () => {
        zoomLevel.current = map.mapInstance.getZoom();
      });

      return () => {
        if (zoomChangeListener) {
          try {
            window.google.maps.event.removeListener(zoomChangeListener);
          } catch (error) {
            captureException(error);
          }
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, alerts, hasGoogleMapIdChanged, selectedAlertID]);

  return <></>;
};

export default React.memo(AlertsGL);
